import * as Sentry from '@sentry/react';

let initialized = false;

/** FRONT-1236: runtime config injected into index.html by the server. Keep the
 *  name in sync with CLIENT_CONFIG_GLOBAL in src/http/client-config.ts. */
interface InjectedClientConfig {
  sentryDsn?: string;
  sentryEnvironment?: string;
  sentryTracesSampleRate?: number;
}

/**
 * Resolve the Sentry settings, preferring the server-injected values over the
 * build-time ones.
 *
 * A deployed bundle can never carry `VITE_*`: Vite inlines those at build time,
 * while Eddy's secrets arrive at container start — which is why frontend Sentry
 * reported nothing on every environment. The server now injects the values into
 * the served HTML instead. `import.meta.env` stays as the fallback so local
 * `bun run dev` (no server injection in front of Vite) still works off .env.
 */
export function resolveSentryConfig(
  injected: InjectedClientConfig | undefined = (
    globalThis as { __EDDY_CLIENT_CONFIG__?: InjectedClientConfig }
  ).__EDDY_CLIENT_CONFIG__,
  buildEnv: Record<string, unknown> = import.meta.env as unknown as Record<string, unknown>,
): { dsn: string; environment: string; tracesSampleRate: number } | null {
  const dsn = injected?.sentryDsn || (buildEnv.VITE_SENTRY_DSN as string | undefined);
  if (!dsn) return null;
  const rawRate =
    injected?.sentryTracesSampleRate ?? buildEnv.VITE_SENTRY_TRACE_SAMPLE_RATE ?? '0.1';
  const rate = Number(rawRate);
  return {
    dsn,
    environment:
      injected?.sentryEnvironment ||
      (buildEnv.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
      'development',
    tracesSampleRate: Number.isFinite(rate) ? rate : 0.1,
  };
}

interface EventWithStack {
  exception?: {
    values?: Array<{ stacktrace?: { frames?: Array<{ filename?: string; lineno?: number }> } }>;
  };
}

/**
 * FRONT-2698: true when every stack frame belongs to a script injected into the
 * page rather than the bundle. A browser extension, a browser's built-in
 * assistant or an automation tool runs code with no source URL, so Chromium
 * names its frames `<anonymous>:line:col` (EDDY-AA: an injected `copy(...)` on
 * prod). Bundle code, eval'd code included, carries the bundle URL.
 *
 * The line number is load-bearing: V8 ALSO names its own built-ins
 * `<anonymous>` (`at JSON.parse (<anonymous>)`), but with no line. A real Eddy
 * error whose captured stack holds only built-ins — `res.text().then(JSON.parse)`
 * — must still be sent, so a frame without a line never counts as injected.
 * An event with no frames at all is kept too: no stack is no evidence.
 * Only Chromium's spelling is matched; other browsers' injected code still reports.
 */
export function isInjectedScriptError(event: EventWithStack): boolean {
  const frames = (event.exception?.values ?? []).flatMap((v) => v.stacktrace?.frames ?? []);
  return (
    frames.length > 0 &&
    frames.every((f) => f.filename === '<anonymous>' && typeof f.lineno === 'number')
  );
}

const SENSITIVE_KEY = /token|key|secret|password|auth|bearer|credential|dsn|apikey/i;
const SENSITIVE_HEADER = /^(authorization|cookie|set-cookie|x-api-key)$/i;

function scrub(obj: unknown, depth: number): void {
  if (depth > 4 || obj === null || typeof obj !== 'object') return;
  const record = obj as Record<string, unknown>;
  for (const k of Object.keys(record)) {
    if (SENSITIVE_KEY.test(k)) {
      record[k] = '[REDACTED]';
    } else {
      scrub(record[k], depth + 1);
    }
  }
}

function scrubHeaders(headers: Record<string, unknown> | undefined): void {
  if (!headers) return;
  for (const k of Object.keys(headers)) {
    if (SENSITIVE_HEADER.test(k)) headers[k] = '[REDACTED]';
  }
}

/**
 * Runs on every outgoing error event: drops injected-script noise (FRONT-2698),
 * then scrubs anything credential-shaped before it leaves the browser.
 */
export function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (isInjectedScriptError(event)) return null;

  scrubHeaders(event.request?.headers as Record<string, unknown> | undefined);
  if (event.request?.data) scrub(event.request.data, 0);
  if (event.request?.query_string) scrub(event.request.query_string, 0);
  if (event.extra) scrub(event.extra, 0);
  if (event.contexts) for (const ctx of Object.values(event.contexts)) scrub(ctx, 0);
  if (event.breadcrumbs) for (const bc of event.breadcrumbs) {
    if (bc.data) scrub(bc.data, 0);
  }
  return event;
}

export function initSentry(): void {
  if (initialized) return;
  const resolved = resolveSentryConfig();
  if (!resolved) {
    console.log('[sentry] no DSN (server-injected or VITE_SENTRY_DSN) — Sentry disabled');
    return;
  }
  Sentry.init({
    dsn: resolved.dsn,
    environment: resolved.environment,
    tracesSampleRate: resolved.tracesSampleRate,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend,
  });
  Sentry.setTag('runtime', 'frontend');
  initialized = true;
}

export { Sentry };
